import { useState, useEffect } from 'react';
import {
  tokens,
  makeStyles,
  shorthands,
  Text,
  Button,
  Tooltip,
  Avatar,
  Popover,
  PopoverTrigger,
  PopoverSurface,
} from '@fluentui/react-components';
import {
  ShieldKeyholeRegular,
  SignOutRegular,
} from '@fluentui/react-icons';
import type { AdminIdentity } from '../types';
import { APP_NAME } from '../constants';
import { getMyPhoto } from '../services/graph';

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    height: '48px',
    ...shorthands.padding('0', '16px'),
    backgroundColor: '#f5f5f5',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    position: 'sticky',
    top: 0,
    zIndex: 1000,
    backdropFilter: 'blur(20px)',
  },
  titleSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  appIcon: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '28px',
    height: '28px',
    borderRadius: '6px',
    backgroundColor: tokens.colorBrandBackground,
    color: '#fff',
    fontSize: '16px',
    flexShrink: 0,
  },
  appName: {
    fontSize: '14px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    whiteSpace: 'nowrap',
  },
  rightSection: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  },
  avatarBtn: {
    minWidth: 'auto',
    ...shorthands.padding('0'),
    ...shorthands.borderRadius('50%'),
    overflow: 'visible',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  popoverSurface: {
    ...shorthands.padding('16px', '20px'),
    width: '320px',
  },
  popoverHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '16px',
    paddingBottom: '16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  popoverInfo: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  popoverName: {
    fontSize: '16px',
    fontWeight: 600,
    color: tokens.colorNeutralForeground1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  popoverEmail: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  signOutBtn: {
    width: '100%',
    justifyContent: 'flex-start',
  },
});

interface AppHeaderProps {
  admin: AdminIdentity | null;
  onSignOut: () => void;
}

export function AppHeader({ admin, onSignOut }: AppHeaderProps) {
  const styles = useStyles();
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);

  useEffect(() => {
    let revoke: string | null = null;
    getMyPhoto().then((url) => {
      if (url) {
        revoke = url;
        setProfilePhoto(url);
      }
    }).catch(() => { /* no photo */ });
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, []);

  return (
    <header className={styles.header}>
      <div className={styles.titleSection}>
        <div className={styles.appIcon}>
          <ShieldKeyholeRegular fontSize={16} />
        </div>
        <Text className={styles.appName}>{APP_NAME}</Text>
      </div>

      {admin && (
        <div className={styles.rightSection}>
          <Popover withArrow positioning="below-end">
            <PopoverTrigger disableButtonEnhancement>
              <Tooltip content={admin.displayName} relationship="label">
                <Button
                  appearance="subtle"
                  className={styles.avatarBtn}
                  icon={
                    <Avatar
                      name={admin.displayName}
                      image={profilePhoto ? { src: profilePhoto } : undefined}
                      size={32}
                      color="brand"
                    />
                  }
                />
              </Tooltip>
            </PopoverTrigger>
            <PopoverSurface className={styles.popoverSurface}>
              <div className={styles.popoverHeader}>
                <Avatar
                  name={admin.displayName}
                  image={profilePhoto ? { src: profilePhoto } : undefined}
                  size={56}
                  color="brand"
                />
                <div className={styles.popoverInfo}>
                  <Text className={styles.popoverName}>
                    {admin.displayName}
                  </Text>
                  <Text className={styles.popoverEmail}>
                    {admin.userPrincipalName}
                  </Text>
                </div>
              </div>
              <Button
                appearance="subtle"
                icon={<SignOutRegular />}
                onClick={onSignOut}
                className={styles.signOutBtn}
              >
                Sign out
              </Button>
            </PopoverSurface>
          </Popover>
        </div>
      )}
    </header>
  );
}
